"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCancelVirtualCardMutationQuery = exports.virtualCardTask = exports.walletDetailsQuery = exports.paginatedWalletListQuery = exports.paginatedMyWalletListQuery = exports.walletKeys = void 0;
const react_query_1 = require("@tanstack/react-query");
const pino_1 = __importDefault(require("pino"));
const virtualCard_1 = require("../components/pages/request/virtualCard");
const cardUtils_1 = require("../components/pages/wallet/myCardsListView/cardUtils");
const strings_1 = require("../constants/strings");
const walletRepository_1 = require("../repositories/walletRepository");
const Root_1 = require("../router/Root");
const zustandStore_1 = require("../zustandStore");
const companyQuery_1 = require("./companyQuery");
const paymentQuery_1 = require("./paymentQuery");
const teamQuery_1 = require("./teamQuery");
const userQuery_1 = require("./userQuery");
exports.walletKeys = {
    getUsersListForActiveTeam: 'getUsersListForActiveTeam',
    getWalletDetails: (taskId) => ['getWalletDetails', taskId],
    getPaginatedWalletList: ({ limit = 10, page = 0, teamId = '', owner = '', status = 'ACTIVE', sortBy = 'CREATION_DATE', sortOrder = 'DESC', } = {}) => ['paginatedWalletList', limit, page, teamId, owner, status, sortBy, sortOrder],
    getVirtualCardtask: (virtualCardId) => ['getVirtualCardtask', virtualCardId],
};
const logger = (0, pino_1.default)();
const paginatedMyWalletListQuery = ({ limit, page, teamId, owner, status, sortBy, sortOrder, }) => {
    const params = { limit, page, teamId, owner, status, sortBy, sortOrder };
    return (0, react_query_1.queryOptions)({
        queryKey: exports.walletKeys.getPaginatedWalletList(params),
        queryFn: async () => {
            try {
                const paginatedWalletData = await walletRepository_1.WalletRepository.getPaginatedWalletList(params);
                if (paginatedWalletData.tasks && paginatedWalletData.tasks.length > 0) {
                    const enhancedTasksPromises = paginatedWalletData.tasks.filter(Boolean).map(async (task) => {
                        let enhancedCardData = task;
                        let card = null;
                        let virtualCard = null;
                        const taskId = task?.request?.requestId || '';
                        try {
                            const user = await Root_1.queryClient.ensureQueryData(userQuery_1.userQuery);
                            const companyPropertiesData = await Root_1.queryClient.ensureQueryData((0, companyQuery_1.companyPropertiesQuery)(user.companyAccountId, { cardprovider: 'tsgo-cardprovider' }));
                            card = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.virtualCardQuery)({
                                documentId: taskId,
                                cardProvider: companyPropertiesData?.cardprovider || '',
                            }));
                            virtualCard = (0, virtualCard_1.getVirtualCard)(card);
                            const updatedCard = virtualCard ? Object.assign({}, task.card, virtualCard) : task.card;
                            enhancedCardData = {
                                ...task,
                                card: updatedCard,
                            };
                        }
                        catch (error) {
                            logger.error({ error, message: 'Failed to fetch virtual card data' });
                            return enhancedCardData;
                        }
                        const walletInfo = {
                            balance: enhancedCardData.card?.balance,
                            amount: enhancedCardData?.request?.amount,
                            authPaddingPercentage: enhancedCardData?.request?.authPaddingPercentage,
                        };
                        let remainingValue = 0;
                        try {
                            remainingValue = (0, cardUtils_1.getRemainingValue)(walletInfo);
                        }
                        catch (error) {
                            logger.error({ error, message: 'Failed to calculate remaining value' });
                            return enhancedCardData;
                        }
                        let remainingCardValue = '';
                        let cardAmount = '';
                        let spentAmount = '';
                        let spent = 0;
                        try {
                            remainingCardValue = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(enhancedCardData.request?.currency || '', remainingValue));
                        }
                        catch (error) {
                            logger.error({ error, message: 'Failed to fetch remaining card value' });
                            return enhancedCardData;
                        }
                        try {
                            cardAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(enhancedCardData?.request?.currency || '', Number(enhancedCardData?.request?.amount || enhancedCardData.request?.fields?.amount) || 0));
                        }
                        catch (error) {
                            logger.error({ error, message: 'Failed to fetch card amount' });
                            return enhancedCardData;
                        }
                        try {
                            spent = (0, cardUtils_1.getSpentAmount)(walletInfo);
                            spentAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(enhancedCardData?.card?.currency || '', spent));
                        }
                        catch (error) {
                            logger.error({ error, message: 'Failed to fetch spent amount' });
                            return enhancedCardData;
                        }
                        return {
                            ...enhancedCardData,
                            remainingCardValue,
                            remainingValue,
                            cardAmount,
                            spentAmount,
                            spent,
                        };
                    });
                    const enhancedTasks = await Promise.all(enhancedTasksPromises);
                    return {
                        count: paginatedWalletData.count,
                        tasks: enhancedTasks,
                        hasNextPage: paginatedWalletData.hasNextPage,
                        // we need this, so we do not disable the next pager when we have null values in tasks
                        initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
                    };
                }
                return {
                    ...paginatedWalletData,
                    hasNextPage: paginatedWalletData.hasNextPage,
                    // we need this, so we do not disable the next pager when we have null values in tasks
                    initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
                };
            }
            catch (error) {
                logger.error({ error });
                throw error;
            }
        },
        placeholderData: react_query_1.keepPreviousData,
    });
};
exports.paginatedMyWalletListQuery = paginatedMyWalletListQuery;
const paginatedWalletListQuery = ({ limit, page, teamId, owner, status, sortBy, sortOrder, }) => {
    const params = { limit, page, teamId, owner, status, sortBy, sortOrder };
    return (0, react_query_1.queryOptions)({
        queryKey: exports.walletKeys.getPaginatedWalletList(params),
        queryFn: async () => {
            const paginatedWalletData = await walletRepository_1.WalletRepository.getPaginatedWalletList(params);
            if (paginatedWalletData.tasks && paginatedWalletData.tasks.length > 0) {
                const enhancedTasksPromises = paginatedWalletData.tasks.filter(Boolean).map(async (task) => {
                    const walletInfo = {
                        balance: task.card?.balance,
                        amount: task?.request?.amount,
                        authPaddingPercentage: task?.request?.authPaddingPercentage,
                    };
                    const remainingValue = (0, cardUtils_1.getRemainingValue)(walletInfo);
                    let remainingCardValue = '';
                    let cardAmount = '';
                    let spentAmount = '';
                    let spent = 0;
                    let cardTeam = null;
                    try {
                        remainingCardValue = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(task?.request?.currency || '', remainingValue || 0));
                        cardAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(task?.request?.currency || '', Number(task?.request?.amount || task?.request?.fields?.amount) || 0));
                        spent = (0, cardUtils_1.getSpentAmount)(walletInfo);
                        spentAmount = await Root_1.queryClient.ensureQueryData((0, paymentQuery_1.currencyAndTotalQuery)(task.card?.currency || '', spent || 0));
                        if (task?.request?.teamId) {
                            cardTeam = await Root_1.queryClient.ensureQueryData((0, teamQuery_1.teamQuery)({ teamId: task?.request?.teamId }));
                        }
                    }
                    catch (error) {
                        logger.error({ error });
                    }
                    return {
                        ...task,
                        remainingCardValue,
                        remainingValue,
                        cardAmount,
                        cardTeam,
                        spentAmount,
                        spent,
                    };
                });
                const enhancedTasks = await Promise.all(enhancedTasksPromises);
                return {
                    count: paginatedWalletData.count,
                    tasks: enhancedTasks,
                    hasNextPage: paginatedWalletData.hasNextPage,
                    // we need this, so we do not disable the next pager when we have null values in tasks
                    initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
                };
            }
            return {
                ...paginatedWalletData,
                hasNextPage: paginatedWalletData.hasNextPage,
                // we need this, so we do not disable the next pager when we have null values in tasks
                initialPaginatedWalletDataCount: Number(paginatedWalletData.tasks?.length) || 0,
            };
        },
        placeholderData: react_query_1.keepPreviousData,
    });
};
exports.paginatedWalletListQuery = paginatedWalletListQuery;
const walletDetailsQuery = (taskId) => (0, react_query_1.queryOptions)({
    queryKey: exports.walletKeys.getWalletDetails(taskId),
    queryFn: () => walletRepository_1.WalletRepository.getWalletDetails({ taskId }),
});
exports.walletDetailsQuery = walletDetailsQuery;
const virtualCardTask = (virtualCardId) => (0, react_query_1.queryOptions)({
    queryKey: exports.walletKeys.getVirtualCardtask(virtualCardId),
    queryFn: () => walletRepository_1.WalletRepository.getVirtualCardTask({ virtualCardId }),
    enabled: Boolean(virtualCardId),
});
exports.virtualCardTask = virtualCardTask;
const useCancelVirtualCardMutationQuery = () => {
    const { setNotification, cards, setCards } = (0, zustandStore_1.useZustand)(state => ({
        setNotification: state.setNotification,
        cards: state.wallet.cards,
        setCards: state.setWalletCards,
    }));
    return (0, react_query_1.useMutation)({
        mutationFn: walletRepository_1.WalletRepository.cancelVirtualCard,
        onSuccess: async (_data, variables) => {
            const updatedCards = cards.filter(item => item.subjectId !== variables.subjectId);
            setCards(updatedCards);
            setNotification([
                {
                    type: strings_1.notificationStrings.SUCCESS,
                    message: strings_1.walletStrings.CANCEL_CARD_SUCCESS,
                },
            ]);
        },
        onError: (_data, _variables, error) => {
            logger.error({ error });
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: strings_1.walletStrings.CANCEL_CARD_ERROR,
                },
            ]);
        },
    });
};
exports.useCancelVirtualCardMutationQuery = useCancelVirtualCardMutationQuery;
